import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore'
import {
  COLLECTIONS,
  mergedReceiptIds,
  transactionSchema,
  type NewTransaction,
  type Transaction,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'
import { converterFor, nowIso } from '@/lib/firestore'

const TIMESTAMP_FIELDS = [
  'transactionDate',
  'dateReceived',
  'reconciledAt',
  'createdAt',
  'updatedAt',
] as const

const transactionConverter = converterFor(transactionSchema, TIMESTAMP_FIELDS)

const transactionsRef = () =>
  collection(db, COLLECTIONS.transactions).withConverter(transactionConverter)

/**
 * Every transaction for a user and fiscal year, newest purchase first.
 *
 * One query per fiscal year rather than one per budget code: §9 budgets under
 * 200 transactions per code per year, so a whole year is still small, and the
 * dashboard needs all of them at once to total every card. Per-code views
 * filter this cached result rather than issuing their own query — which is
 * what makes the code detail screen open without a spinner (§7.5).
 */
export async function fetchTransactions(
  userId: string,
  fiscalYear: number,
): Promise<Transaction[]> {
  const snapshot = await getDocs(
    query(
      transactionsRef(),
      where('userId', '==', userId),
      where('fiscalYear', '==', fiscalYear),
      orderBy('transactionDate', 'desc'),
    ),
  )
  return snapshot.docs.map((d) => d.data())
}

/**
 * Specific charges by id, whatever fiscal year they are in.
 *
 * A reconciliation packet is a fixed list of charges, and a P-Card cycle
 * straddles the July rollover — the July statement carries charges from the
 * last days of June, which belong to the year that just ended. Reading the
 * packet's charges out of one year's list drops those, so the screen would
 * show an item missing while `generatePacket`, which fetches by id, prints it
 * perfectly well. A packet screen that disagrees with the packet is worse than
 * one that is merely slow.
 *
 * Individual `get`s rather than an `in` query: a packet is tens of charges,
 * not thousands, and a document read needs no composite index — which the
 * emulator would not have caught the absence of anyway (see CLAUDE.md).
 */
export async function fetchTransactionsByIds(
  ids: readonly string[],
): Promise<Transaction[]> {
  if (ids.length === 0) return []
  const snapshots = await Promise.all(
    ids.map((id) =>
      getDoc(doc(db, COLLECTIONS.transactions, id).withConverter(transactionConverter)),
    ),
  )
  return snapshots
    .filter((snapshot) => snapshot.exists())
    .map((snapshot) => snapshot.data()!)
}

/**
 * Write a charge and return it as it was stored.
 *
 * The whole document, not just the id: the caller puts it straight into the
 * cached year so the lists are right the moment the panel closes, rather than
 * one refetch later. See `useCreateTransaction`.
 */
export async function createTransaction(
  userId: string,
  fiscalYear: number,
  input: NewTransaction,
): Promise<Transaction> {
  const timestamp = nowIso()
  const stored = {
    ...input,
    userId,
    fiscalYear,
    sourceRef: null,
    status: 'pending',
    reconciledAt: null,
    reconciledVia: null,
    csiuJournalNumber: null,
    receiptIds: [],
    splitGroupId: null,
    reversalOfTransactionId: null,
    noReceiptReason: null,
    noReceiptExplanation: null,
    // Set only when a statement line claims this charge — the log sheet
    // prints the statement's spelling in preference to `vendor` (§8.1).
    statementVendorName: null,
    createdAt: timestamp,
    updatedAt: timestamp,
  } as Transaction
  const ref = await addDoc(transactionsRef(), stored)
  return { ...stored, id: ref.id }
}

export type TransactionUpdate = Partial<
  Pick<
    Transaction,
    | 'budgetCodeId'
    | 'transactionDate'
    | 'dateReceived'
    | 'amount'
    | 'vendor'
    | 'description'
    | 'notes'
    | 'source'
    | 'status'
    // Editable because the derived year is right almost always and wrong
    // exactly where it matters: a cycle that straddles 1 July puts June
    // charges on a July statement, and which budget they came out of is the
    // business office's call, not a calculation's.
    | 'fiscalYear'
  >
>

export async function updateTransaction(
  id: string,
  patch: TransactionUpdate,
): Promise<void> {
  const { transactionDate, dateReceived, ...rest } = patch
  await updateDoc(doc(db, COLLECTIONS.transactions, id), {
    ...rest,
    ...(transactionDate !== undefined ? { transactionDate: new Date(transactionDate) } : {}),
    ...(dateReceived !== undefined ? { dateReceived: new Date(dateReceived) } : {}),
    updatedAt: new Date(),
  })
}

/**
 * Refusing to delete a charge that has already been handed in.
 *
 * A class rather than a returned flag so a caller cannot ignore it by
 * accident, and so the screen can tell this apart from a network failure.
 */
export class ChargeAlreadySubmitted extends Error {
  readonly cycleEnd: string

  constructor(cycleEnd: string) {
    super(
      'This charge is on a packet that has already been submitted, so it cannot be deleted. Void it instead — that keeps the row and removes it from every total.',
    )
    this.name = 'ChargeAlreadySubmitted'
    this.cycleEnd = cycleEnd
  }
}

/**
 * Delete a charge — unless it has already been handed in.
 *
 * A submitted packet is a document the business office holds. Its
 * `orderedTransactionIds` still name this charge, so deleting it makes the
 * stored packet disagree with the ledger, and regenerating that period would
 * quietly produce a *shorter* packet than the one submitted — no warning, no
 * trace, and nothing on screen to suggest an item went missing.
 *
 * Void is the operation that was wanted in that case: §5.3's status for a
 * charge that was reversed, which keeps the row and removes it from every
 * total. Delete stays what it is for — something entered by mistake.
 *
 * Checked against the periods rather than by a query on
 * `orderedTransactionIds`: an `array-contains` combined with an equality
 * filter needs a composite index, and the emulator would not have told us it
 * was missing (see CLAUDE.md). The period collection is a dozen documents.
 */
export async function deleteTransaction(userId: string, id: string): Promise<void> {
  const periods = await getDocs(
    query(
      collection(db, COLLECTIONS.reconciliationPeriods),
      where('userId', '==', userId),
    ),
  )

  const submitted = periods.docs.find(
    (period) =>
      period.get('status') === 'submitted' &&
      ((period.get('orderedTransactionIds') as string[] | null) ?? []).includes(id),
  )
  if (submitted !== undefined) {
    throw new ChargeAlreadySubmitted((submitted.get('cycleEnd') as string) ?? '')
  }

  await deleteDoc(doc(db, COLLECTIONS.transactions, id))
}

/** Refused rather than attempted: see `mergeCharges`. */
export class MergeTouchesSubmittedPacket extends Error {
  readonly cycleEnd: string

  constructor(cycleEnd: string) {
    super(
      'One of these charges is on a packet that has already been submitted, so they cannot be merged. The business office is holding a copy that names it — correct the amounts by hand instead.',
    )
    this.name = 'MergeTouchesSubmittedPacket'
    this.cycleEnd = cycleEnd
  }
}

/**
 * Fold one charge into another — two rows that were always one purchase.
 *
 * The survivor is the caller's choice and is always the larger amount: a tip
 * is added after the itemised slip prints, and the card is charged the total,
 * so that is the figure the statement will show and the only one a
 * reconciliation can match.
 *
 * Everything the removed charge carried comes across:
 *
 * - **Its documents**, appended to the survivor's. Losing the itemised slip
 *   is the thing this whole area exists to prevent.
 * - **Each receipt's primary charge**, repointed when — and only when — it
 *   named the charge being removed. A receipt whose primary is some third
 *   charge is none of this merge's business, and one already pointing at the
 *   survivor needs nothing.
 *
 * **Run as a transaction, not a batch.** A batch writes what was read moments
 * earlier, and `receiptIds` is written whole rather than with `arrayUnion`,
 * so a receipt attached from another device between the read and the commit
 * was silently dropped — a phone and a desktop open at once is Matt's normal
 * state. A transaction re-reads and retries instead.
 *
 * Refused when *either* charge is on a submitted packet. The removed one for
 * the reason `deleteTransaction` gives; the survivor because it gains receipt
 * pages, and regenerating that period would then print a packet that differs
 * from the copy handed in.
 */
export async function mergeCharges(
  userId: string,
  keepId: string,
  removeId: string,
): Promise<void> {
  if (keepId === removeId) throw new Error('A charge cannot be merged into itself.')

  // Queries are not available inside a transaction, so the packet check runs
  // before it — the same compromise `deleteTransaction` makes.
  const periods = await getDocs(
    query(collection(db, COLLECTIONS.reconciliationPeriods), where('userId', '==', userId)),
  )
  const submitted = periods.docs.find((period) => {
    if (period.get('status') !== 'submitted') return false
    const ids = (period.get('orderedTransactionIds') as string[] | null) ?? []
    return ids.includes(removeId) || ids.includes(keepId)
  })
  if (submitted !== undefined) {
    throw new MergeTouchesSubmittedPacket((submitted.get('cycleEnd') as string) ?? '')
  }

  const keepRef = doc(db, COLLECTIONS.transactions, keepId)
  const removeRef = doc(db, COLLECTIONS.transactions, removeId)

  await runTransaction(db, async (tx) => {
    // Every read first: Firestore refuses a read after a write in the same
    // transaction.
    const [keepSnap, removeSnap] = await Promise.all([tx.get(keepRef), tx.get(removeRef)])
    if (!keepSnap.exists() || !removeSnap.exists()) {
      throw new Error('One of those charges no longer exists. Reload and try again.')
    }

    const keepReceiptIds = (keepSnap.get('receiptIds') as string[] | null) ?? []
    const removeReceiptIds = (removeSnap.get('receiptIds') as string[] | null) ?? []

    // A receipt id naming a document that is gone cannot be read *or*
    // skipped here: the rules answer a read of a missing document with a
    // denial, which fails the whole transaction. Stale ids are prevented at
    // the source instead — `purgeReceiptRecord` now clears the id from every
    // charge that lists it.
    const receiptRefs = removeReceiptIds.map((id) => doc(db, COLLECTIONS.receipts, id))
    const receiptSnaps = await Promise.all(receiptRefs.map((ref) => tx.get(ref)))

    const timestamp = new Date(nowIso())

    tx.update(keepRef, {
      receiptIds: mergedReceiptIds(keepReceiptIds, removeReceiptIds),
      updatedAt: timestamp,
    })

    receiptSnaps.forEach((snap, index) => {
      if (!snap.exists()) return
      // The condition the comment above describes, asked of the receipt
      // itself. Testing whether the survivor already held it answered a
      // different question, and answered it wrongly in both directions.
      if (snap.get('linkedTransactionId') !== removeId) return
      tx.update(receiptRefs[index]!, {
        linkedTransactionId: keepId,
        updatedAt: timestamp,
      })
    })

    tx.delete(removeRef)
  })
}
